import React, { useState, useRef, useEffect } from 'react';
import { Member } from '../../types';
import './Modal.css';
import { displayPersonName } from '../../utils/civilDataErased';
import { getCurrentUser } from '../../api/Authentication';
import { getPersonalUserRoles } from '../../api/RegistrationRessource';
import { getSchoolLevels, createLevelStudent } from '../../api/SchoolDashboard/Levels';
import { getTeacherClasses } from '../../api/Dashboard';
import { useAppContext } from '../../context/AppContext';
import { useToast } from '../../hooks/useToast';
import { getSelectedSchoolId } from '../../utils/contextUtils';
import QRCodePrintModal from './QRCodePrintModal';
import AvatarImage from '../UI/AvatarImage';

interface AddMemberModalProps {
  onClose: () => void;
  onAdd: (member: Omit<Member, 'id'>) => void;
  onSuccess?: () => void;
}

// Traduction identique à celle de PersonalUserRegisterForm.tsx pour l'affichage
const tradFR: Record<string, string> = {
  parent: "Parent",
  grand_parent: "Grand-parent",
  children: "Enfant",
  voluntary: "Volontaire",
  tutor: "Tuteur",
  employee: "Salarié",
  other: "Autre",
  // Garder des fallbacks pour l'ancien système au cas où
  admin: "Admin",
  referent: "Référent",
  member: "Membre"
};

/** Set to true to show the INE (Identifiant National Élève) field again. */
const SHOW_INE_FIELD = false;

const isMinorByBirthday = (birthday: string): boolean => {
  if (!birthday) return false;
  const bday = new Date(birthday);
  const today = new Date();
  let age = today.getFullYear() - bday.getFullYear();
  const monthDiff = today.getMonth() - bday.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < bday.getDate())) {
    age -= 1;
  }
  return age < 15;
};

const AddMemberModal: React.FC<AddMemberModalProps> = ({ onClose, onAdd, onSuccess }) => {
  const { state } = useAppContext();
  const isTeacherContext = state.showingPageType === 'teacher';
  const { showSuccess, showError, showInfo } = useToast();
  const [selectedSchoolId, setSelectedSchoolId] = useState<number | null>(null);
  const [availableSchools, setAvailableSchools] = useState<Array<{ id: number; name: string }>>([]);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    birthday: '',
    guardianEmail: '',
    ine: '',
    role: 'member',
    roleAdditionalInfo: '',
    levelId: '',
    avatar: ''
  });
  const [ineError, setIneError] = useState<string | null>(null);

  const [avatarPreview, setAvatarPreview] = useState<string>('');
  const [isUsingDefaultAvatar, setIsUsingDefaultAvatar] = useState<boolean>(true);

  // State pour les données API
  const [apiRoles, setApiRoles] = useState<{ value: string; requires_additional_info: boolean }[]>([]);
  const studentRoles = ['eleve_primaire', 'collegien', 'lyceen', 'etudiant'];
  const [levels, setLevels] = useState<{ id: number; name: string; level: string; school_id?: number | null }[]>([]);
  const [loadingLevels, setLoadingLevels] = useState(false);

  // State pour le QR Code
  const [showQRCodeModal, setShowQRCodeModal] = useState<boolean>(false);
  const [studentData, setStudentData] = useState<{ claimToken: string; fullName: string } | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);

  // Chargement des écoles disponibles pour les teachers (toutes les écoles confirmées)
  useEffect(() => {
    const fetchSchools = async () => {
      if (!isTeacherContext) return;
      
      try {
        const currentUser = await getCurrentUser();
        const schools = currentUser.data?.available_contexts?.schools || [];
        
        // Inclure toutes les écoles confirmées (pas de filtre de rôle)
        const schoolsList = schools.map((school: any) => ({
          id: school.id,
          name: school.name
        }));
        
        setAvailableSchools(schoolsList);
        // Teacher : sélectionner le premier établissement par défaut s'il y en a plusieurs
        setSelectedSchoolId(schoolsList.length > 0 ? schoolsList[0].id : null);
      } catch (error) {
        console.error("Erreur lors du chargement des écoles", error);
      }
    };
    
    fetchSchools();
  }, [isTeacherContext]);

  // Chargement des Roles (une seule fois au montage)
  useEffect(() => {
    const fetchRoles = async () => {
      if (apiRoles.length > 0) return; // Déjà chargés
      
      try {
        const rolesRes = await getPersonalUserRoles();
        const rolesData = rolesRes?.data?.data ?? rolesRes?.data ?? rolesRes ?? [];
        if (Array.isArray(rolesData)) {
          const filtered = rolesData.filter((role: any) => studentRoles.includes(role.value));
          setApiRoles(filtered);
          // Sélectionner le premier rôle étudiant par défaut si disponible
          if (filtered.length > 0) {
            setFormData(prev => ({ ...prev, role: filtered[0].value }));
          }
        }
      } catch (error) {
        console.error("Erreur chargement des rôles", error);
      }
    };
    fetchRoles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Chargement des Levels/Classes en fonction de l'école sélectionnée ou toutes les classes du teacher
  useEffect(() => {
    let isMounted = true;
    
    const fetchLevels = async () => {
      try {
        if (isMounted) {
          setLoadingLevels(true);
          setLevels([]); // Vider la liste pendant le chargement
          // Ne pas réinitialiser levelId ici pour éviter de perdre la sélection de l'utilisateur
          // On le réinitialisera seulement si nécessaire (changement d'école)
        }

        // Si c'est un teacher, utiliser l'API teachers/classes pour récupérer toutes ses classes
        if (isTeacherContext) {
          console.log('Fetching levels for teacher', levels);
          const levelsRes = await getTeacherClasses(1, 100);
          const levelsData = levelsRes?.data?.data ?? levelsRes?.data ?? [];
          
          if (isMounted) {
            if (Array.isArray(levelsData)) {
              const mappedLevels = levelsData.map((l: any) => ({
                id: l.id,
                name: l.name,
                level: l.level || l.level_name || '',
                school_id: l.school_id || null
              }));
              
              // Filtrer les classes en fonction de l'école sélectionnée
              let filteredLevels = mappedLevels;
              if (selectedSchoolId === null) {
                // Si "Aucun" est sélectionné, montrer uniquement les classes indépendantes (school_id null)
                filteredLevels = mappedLevels.filter((l) => l.school_id === null || l.school_id === undefined);
              } else if (selectedSchoolId !== null) {
                // Si une école est sélectionnée, montrer uniquement les classes de cette école
                filteredLevels = mappedLevels.filter((l) => l.school_id !== null && l.school_id !== undefined && Number(l.school_id) === selectedSchoolId);
              }
              
              setLevels(filteredLevels);
              // Sélectionner automatiquement la première classe par défaut si aucune n'est déjà sélectionnée
              setFormData(prev => {
                if (prev.levelId) {
                  // Vérifier si la classe sélectionnée existe toujours dans la nouvelle liste filtrée
                  const levelExists = filteredLevels.some(l => l.id.toString() === prev.levelId);
                  if (!levelExists) {
                    // Si la classe sélectionnée n'existe plus, sélectionner la première disponible
                    return { ...prev, levelId: filteredLevels.length > 0 ? filteredLevels[0].id.toString() : '' };
                  }
                  return prev;
                } else {
                  // Si aucune classe n'est sélectionnée, sélectionner la première par défaut
                  return { ...prev, levelId: filteredLevels.length > 0 ? filteredLevels[0].id.toString() : '' };
                }
              });
            } else {
              setLevels([]);
              setFormData(prev => ({ ...prev, levelId: '' }));
            }
          }
        } else {
          // Pour les admins d'école, utiliser l'API schools/levels
          const currentUser = await getCurrentUser();
          const schoolId = getSelectedSchoolId(currentUser.data, state.showingPageType);

          if (!schoolId) {
            if (isMounted) {
              setLevels([]);
              setFormData(prev => ({ ...prev, levelId: '' }));
            }
            return;
          }

          const levelsRes = await getSchoolLevels(Number(schoolId), 1, 100);
          const levelsData = levelsRes?.data?.data ?? levelsRes?.data ?? [];
          
          if (isMounted) {
            if (Array.isArray(levelsData)) {
              const mappedLevels = levelsData.map((l: any) => ({
                id: l.id,
                name: l.name,
                level: l.level
              }));
              setLevels(mappedLevels);
              // Sélectionner le premier level par défaut seulement si aucune classe n'est déjà sélectionnée
              setFormData(prev => {
                if (!prev.levelId && mappedLevels.length > 0) {
                  return { ...prev, levelId: mappedLevels[0].id.toString() };
                }
                // Vérifier si la classe sélectionnée existe toujours dans la nouvelle liste
                if (prev.levelId) {
                  const levelExists = mappedLevels.some(l => l.id.toString() === prev.levelId);
                  if (!levelExists) {
                    // Si la classe sélectionnée n'existe plus, sélectionner la première
                    return { ...prev, levelId: mappedLevels.length > 0 ? mappedLevels[0].id.toString() : '' };
                  }
                }
                return prev;
              });
            } else {
              setLevels([]);
              setFormData(prev => ({ ...prev, levelId: '' }));
            }
          }
        }
      } catch (error) {
        if (isMounted) {
          console.error("Erreur chargement des classes", error);
          showError("Erreur lors du chargement des classes");
          setLevels([]);
          setFormData(prev => ({ ...prev, levelId: '' }));
        }
      } finally {
        if (isMounted) {
          setLoadingLevels(false);
        }
      }
    };
    
    fetchLevels();
    
    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSchoolId, isTeacherContext]);


  // Mise à jour de la génération d'avatar pour accepter les clés dynamiques
  const generateDefaultAvatar = (role: string, firstName: string, lastName: string) => {
    // Mapping de couleurs basique selon les rôles courants
    const colors: Record<string, string> = {
      'voluntary': '#3b82f6', // Bleu (Volontaire/Membre)
      'employee': '#fb923c',  // Orange
      'parent': '#22c55e',    // Vert
      'tutor': '#a855f7',     // Violet
      'default': '#6b7280'    // Gris par défaut
    };

    const initials = `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
    const color = colors[role] || colors['default'];

    return `data:image/svg+xml;base64,${btoa(`
      <svg width="100" height="100" xmlns="http://www.w3.org/2000/svg">
        <circle cx="50" cy="50" r="50" fill="${color}"/>
        <text x="50" y="60" text-anchor="middle" fill="white" font-family="Arial, sans-serif" font-size="32" font-weight="bold">${initials}</text>
      </svg>
    `)}`;
  };

  // Fonction pour capitaliser la première lettre de chaque mot
  const capitalizeName = (str: string): string => {
    return str
      .toLowerCase()
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData(prev => ({ ...prev, [name]: checked }));
    } else {
      // Capitaliser automatiquement le prénom et le nom
      const processedValue = (name === 'firstName' || name === 'lastName') 
        ? capitalizeName(value) 
        : value;
      
      // Debug pour levelId
      if (name === 'levelId') {
        console.log('Setting levelId to:', processedValue);
      }
      
      setFormData(prev => {
        const updated = { ...prev, [name]: processedValue };
        if (name === 'levelId') {
          console.log('Updated formData.levelId:', updated.levelId);
        }
        return updated;
      });

      // Mise à jour de l'avatar si on change le nom/rôle
      if (isUsingDefaultAvatar && (name === 'role' || name === 'firstName' || name === 'lastName')) {
        const firstName = name === 'firstName' ? processedValue : formData.firstName;
        const lastName = name === 'lastName' ? processedValue : formData.lastName;
        const role = name === 'role' ? value : (formData.role || 'eleve_primaire');

        if (firstName && lastName) {
          const newAvatar = generateDefaultAvatar(role, firstName, lastName);
          setAvatarPreview(newAvatar);
        }
      }
    }
  };

  const handleAvatarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        const result = event.target?.result as string;
        setAvatarPreview(result);
        setFormData(prev => ({ ...prev, avatar: result }));
        setIsUsingDefaultAvatar(false);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleResetToDefaultAvatar = () => {
    if (formData.firstName && formData.lastName) {
      const defaultAvatar = generateDefaultAvatar(formData.role || 'eleve_primaire', formData.firstName, formData.lastName);
      setAvatarPreview(defaultAvatar);
      setFormData(prev => ({ ...prev, avatar: '' }));
      setIsUsingDefaultAvatar(true);
    }
  };

  const INE_FORMAT_REGEX = /^(\d{10}[A-Za-z]|\d{9}[A-Za-z]{2})$/;
  const INE_ERROR_FORMAT = "L'INE doit comporter exactement 11 caractères : soit 10 chiffres et 1 lettre, soit 9 chiffres et 2 lettres.";
  const INE_ERROR_UNIQUE = "Cet identifiant national élève (INE) est déjà utilisé par un autre compte.";

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIneError(null);
    console.log('Form data:', formData);
    console.log('LevelId:', formData.levelId);
    console.log('levels:', levels);
    if (!formData.firstName || !formData.lastName || !formData.levelId) {
      showError('Prénom, nom et classe sont obligatoires');
      return;
    }
    
    if (!formData.email && !formData.birthday) {
      showError('Email ou date de naissance est obligatoire');
      return;
    }

    if (formData.ine.trim()) {
      if (formData.ine.length !== 11 || !INE_FORMAT_REGEX.test(formData.ine.trim())) {
        setIneError(INE_ERROR_FORMAT);
        return;
      }
    }

    // Teacher : un établissement est obligatoire ; bloquer si aucun disponible ou non sélectionné
    if (isTeacherContext) {
      if (availableSchools.length === 0) {
        showError('Aucun établissement disponible. Impossible d\'ajouter le membre.');
        return;
      }
      if (!selectedSchoolId) {
        showError('Veuillez sélectionner un établissement');
        return;
      }
    }

    try {
      let response: any;
      
      {
        // Sinon, utiliser l'API schools/:schoolId/levels/:levelId/students
        let schoolId: number | null = null;
        
        if (isTeacherContext) {
          // Pour les teachers avec école sélectionnée, utiliser l'école sélectionnée
          schoolId = selectedSchoolId;
          
          // Si toujours pas d'école, essayer de récupérer depuis la classe
          if (!schoolId) {
            const selectedLevel = levels.find(l => l.id.toString() === formData.levelId);
            if (selectedLevel && selectedLevel.school_id) {
              schoolId = selectedLevel.school_id;
            }
          }
        } else {
          // Pour les admins d'école, utiliser la première école disponible
          const currentUser = await getCurrentUser();
          schoolId = getSelectedSchoolId(currentUser.data, state.showingPageType);
        }

        // Pour les admins d'école, schoolId est obligatoire
        if (!isTeacherContext && !schoolId) {
          showError("Impossible de récupérer l'identifiant de l'école");
          return;
        }

        if (!schoolId) {
          showError("Impossible de récupérer l'identifiant de l'école");
          return;
        }

        // 2. Préparation du payload pour l'API school
        const apiPayload = {
          student: {
            first_name: formData.firstName,
            last_name: formData.lastName,
            email: formData.email || undefined,
            birthday: formData.birthday,
            role: formData.role,
            role_additional_information: formData.roleAdditionalInfo || undefined,
            ine: formData.ine || undefined,
            guardian_email: formData.guardianEmail.trim() || undefined
          }
        };

        console.log('Payload school envoyé:', apiPayload);
        console.log('SchoolId:', schoolId, 'LevelId:', formData.levelId);

        // 3. Envoi de la requête
        response = await createLevelStudent(
          Number(schoolId),
          Number(formData.levelId),
          apiPayload
        );
      }
      
      console.log('Response complète:', response);
      console.log('Response data:', response.data);

      // 4. Extraction du claim_token
      const claimToken = response.data?.data?.claim_token;
      const fullName = displayPersonName(response.data?.data?.full_name, null, null, `${formData.firstName} ${formData.lastName}`);

      console.log('Claim token:', claimToken);
      console.log('Full name:', fullName);

      // 5. Mise à jour de l'UI locale
      const finalAvatar = formData.avatar || generateDefaultAvatar(
        formData.role,
        formData.firstName,
        formData.lastName
      );

      const memberData = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        avatar: finalAvatar,
        email: response.data?.data?.email || formData.email || '',
        profession: formData.roleAdditionalInfo || '',
        roles: [formData.role],
        skills: [],
        availability: [],
        isTrusted: false,
        organization: '',
        badges: [],
        canProposeStage: false,
        canProposeAtelier: false,
        claim_token: claimToken,
        hasTemporaryEmail: response.data?.data?.has_temporary_email || false,
        birthday: formData.birthday,
        role: formData.role,
        levelId: formData.levelId,
        roleAdditionalInfo: formData.roleAdditionalInfo
      };

      onAdd(memberData);

      if (response.data?.existing_user_linked) {
        showInfo('Cet élève existait déjà dans le système.');
      }
      showSuccess(`Élève ${fullName} ajouté avec succès !`);

      // 7. Refetch des données
      if (onSuccess) {
        onSuccess();
      }

      // 8. Afficher le modal QR code si le token est disponible
      if (claimToken) {
        setStudentData({ claimToken, fullName });
        setShowQRCodeModal(true);
        // Ne pas fermer le modal principal encore
      } else {
        console.warn('Pas de claim_token dans la réponse');
        onClose();
      }

    } catch (error: any) {
      console.error("Erreur lors de l'ajout de l'étudiant", error);
      console.error("Erreur détails:", error.response?.data);
      const details = error.response?.data?.details;
      const message = error.response?.data?.message;
      const detailStr = Array.isArray(details) ? details.join(' ') : '';
      const isIneUniqueError = typeof detailStr === 'string' && detailStr.toLowerCase().includes('ine') && detailStr.includes('déjà utilisé');
      const isIneFormatError = typeof detailStr === 'string' && detailStr.toLowerCase().includes('ine') && detailStr.includes('11 caractères');
      if (isIneUniqueError) {
        setIneError(INE_ERROR_UNIQUE);
        showError(INE_ERROR_UNIQUE);
      } else if (isIneFormatError) {
        setIneError(INE_ERROR_FORMAT);
        showError(INE_ERROR_FORMAT);
      } else {
        setIneError(null);
        showError(message || "Erreur lors de l'enregistrement de l'étudiant.");
      }
    }
  };

  // Si le modal QR code doit être affiché, afficher seulement celui-là
  if (showQRCodeModal && studentData) {
    return (
      <QRCodePrintModal
        onClose={() => {
          setShowQRCodeModal(false);
          onClose();
        }}
        claimToken={studentData.claimToken}
        studentName={studentData.fullName}
      />
    );
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Ajouter un nouvel élève</h2>
          <button className="modal-close" onClick={onClose}>
            <i className="fas fa-times"></i>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="modal-body">
          {/* Avatar Section */}
          {/* <div className="form-section">
            <h3>Photo de profil</h3>
            <div className="avatar-selection">
              <div className="avatar-preview">
                <AvatarImage src={avatarPreview} alt="Avatar preview" className="avatar-image" />
              </div>
              <div className="avatar-actions">
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="btn btn-outline btn-sm"
                >
                  <i className="fas fa-upload"></i>
                  Choisir une photo
                </button>
                {!isUsingDefaultAvatar && (
                  <button
                    type="button"
                    onClick={handleResetToDefaultAvatar}
                    className="btn btn-outline btn-sm"
                  >
                    <i className="fas fa-undo"></i>
                    Avatar par défaut
                  </button>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleAvatarChange}
                  style={{ display: 'none' }}
                />
                <p className="avatar-note">
                  Avatar par défaut généré selon le rôle sélectionné
                </p>
              </div>
            </div>
          </div> */}

          <div className="form-grid">
            <div className="form-group">
              <label htmlFor="firstName">Prénom *</label>
              <input
                type="text"
                id="firstName"
                name="firstName"
                value={formData.firstName}
                onChange={handleInputChange}
                required
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label htmlFor="lastName">Nom *</label>
              <input
                type="text"
                id="lastName"
                name="lastName"
                value={formData.lastName}
                onChange={handleInputChange}
                required
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label htmlFor="email">Email</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                className="form-input"
                placeholder="Optionnel - laisser vide pour créer un email temporaire"
              />
            </div>
            
            {/* Sélecteur d'école pour les teachers (toujours affiché) */}
            {isTeacherContext && (
              <div className="form-group">
                <label htmlFor="schoolId">Établissement *</label>
                <select
                  id="schoolId"
                  name="schoolId"
                  value={selectedSchoolId || ''}
                  onChange={(e) => {
                    const value = e.target.value;
                    const schoolId = value === '' ? null : Number(value);
                    setSelectedSchoolId(schoolId);
                    setFormData(prev => ({ ...prev, levelId: '' }));
                  }}
                  className="form-select"
                  required
                >
                  {availableSchools.length === 0 ? (
                    <option value="">Aucun établissement disponible</option>
                  ) : (
                    availableSchools.map((school) => (
                      <option key={school.id} value={school.id}>
                        {school.name}
                      </option>
                    ))
                  )}
                </select>
              </div>
            )}

            <div className="form-group">
              <label htmlFor="birthday">
                Date de naissance {!formData.email && '*'}
              </label>
              <input
                type="date"
                id="birthday"
                name="birthday"
                max={new Date().toISOString().split('T')[0]}
                value={formData.birthday}
                onChange={handleInputChange}
                required={!formData.email}
                className="form-input"
              />
              {!formData.email && (
                <p className="form-hint">La date de naissance est obligatoire.</p>
              )}
            </div>

            {studentRoles.includes(formData.role) &&
              formData.birthday &&
              isMinorByBirthday(formData.birthday) && (
              <div className="form-group">
                <label htmlFor="guardianEmail">Email représentant légal (optionnel)</label>
                <input
                  type="email"
                  id="guardianEmail"
                  name="guardianEmail"
                  value={formData.guardianEmail}
                  onChange={handleInputChange}
                  className="form-input"
                  placeholder="parent@example.com"
                />
                <p className="form-hint">
                  Facultatif pour les élèves de moins de 15 ans. Si renseigné, le représentant légal recevra les notifications.
                </p>
              </div>
            )}

            {SHOW_INE_FIELD && (
              <div className="form-group">
                <label htmlFor="ine">INE (Identifiant National Élève)</label>
                <input
                  type="text"
                  id="ine"
                  name="ine"
                  value={formData.ine}
                  onChange={(e) => {
                    handleInputChange(e);
                    setIneError(null);
                  }}
                  className="form-input"
                  placeholder="11 caractères (optionnel)"
                  maxLength={11}
                />
                <p className="form-hint">Optionnel. 10 chiffres + 1 lettre, ou 9 chiffres + 2 lettres.</p>
                {ineError && (
                  <p className="form-error" role="alert">
                    {ineError}
                  </p>
                )}
              </div>
            )}

            <div className="form-group">
              <label htmlFor="levelId">Classe *</label>
              <select
                id="levelId"
                name="levelId"
                value={formData.levelId || ''}
                onChange={handleInputChange}
                required
                className="form-select"
                disabled={loadingLevels}
              >
                {loadingLevels ? (
                  <option value="">Chargement des classes...</option>
                ) : levels.length > 0 ? (
                  levels.map((level) => (
                    <option key={level.id} value={level.id.toString()}>
                      {level.name} {level.level ? `- ${level.level}` : ''}
                    </option>
                  ))
                ) : (
                  <option value="">Aucune classe disponible</option>
                )}
              </select>
            </div>

            {/* Remplacement du Select Statique par les données de l'API */}
            <div className="form-group">
              <label htmlFor="role">Rôle *</label>
              <select
                id="role"
                name="role"
                value={formData.role || ''}
                onChange={handleInputChange}
                required
                className="form-select"
              >
                {apiRoles.length > 0 ? (
                  apiRoles.map((role) => (
                    <option key={role.value} value={role.value}>
                      {tradFR[role.value] || role.value}
                    </option>
                  ))
                ) : (
                  <option value="">Chargement...</option>
                )}
              </select>
            </div>
          </div>


        </form>

        <div className="modal-footer">
          <button type="button" className="btn btn-outline" onClick={onClose}>
            Annuler
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            onClick={handleSubmit}
            disabled={isTeacherContext && (availableSchools.length === 0 || selectedSchoolId === null)}
          >
            <i className="fas fa-plus"></i>
            Ajouter le membre
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddMemberModal;